// PrismExplorer.js
import { useState, useEffect, useRef } from "react";
import * as d3 from "d3";

export default function PrismExplorer() {
  // shape selector
  const [shape, setShape] = useState("rectangular");

  // rectangular dims
  const [length, setLength] = useState(4);
  const [width,  setWidth ] = useState(3);
  const [height, setHeight] = useState(2);

  // triangular prism dims (right triangle base × height, extruded by prismLength)
  const [base,        setBase       ] = useState(3);
  const [triHeight,  setTriHeight  ] = useState(2);
  const [prismLength, setPrismLength] = useState(4);

  // units & scale
  const [unit, setUnit] = useState("cm");
  const scale = 20;  // px per “1 unit”

  const svgRef = useRef(null);
  const svgW = 500, svgH = 300;

  // Volume & surface‑area formulas
  const volume =
    shape === "rectangular"
      ? length * width * height
      : 0.5 * base * triHeight * prismLength;

  const surfaceArea =
    shape === "rectangular"
      ? 2 * (length*width + length*height + width*height)
      : (() => {
          const endArea = 0.5 * base * triHeight;
          const hyp = Math.hypot(base, triHeight);
          // lateral faces sum of three rectangles:
          const lateral =
            base*prismLength +
            triHeight*prismLength +
            hyp*prismLength;
          return 2*endArea + lateral;
        })();

  // redraw net on any dimension or shape change
  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    if (shape === "rectangular") {
      // --- rectangular net ---
      const frontX = 150, frontY = 80;
      const wPx = length*scale, hPx = height*scale;
      const g = svg.append("g");

      function drawFace(x, y, w, h, label) {
        g.append("rect")
         .attr("x", x).attr("y", y)
         .attr("width", w).attr("height", h)
         .attr("fill", "#ffebee")
         .attr("stroke", "#b71c1c")
         .attr("stroke-width", 2);

        g.append("text")
         .attr("x", x + w/2).attr("y", y + h/2 + 5)
         .attr("fill", "#444")
         .attr("font-size", 14)
         .attr("text-anchor", "middle")
         .text(label);
      }

      // front, left, right, back, top, bottom
      drawFace(frontX, frontY,         wPx, hPx,        "Front");
      drawFace(frontX - wPx, frontY,   wPx, hPx,        "Left");
      drawFace(frontX + wPx, frontY,   wPx, hPx,        "Right");
      drawFace(frontX + wPx*2, frontY, wPx, hPx,        "Back");
      // top and bottom
      const topH = width*scale;
      drawFace(frontX, frontY - topH,  wPx, topH,        "Top");
      drawFace(frontX, frontY + hPx,   wPx, topH,        "Bottom");
    } else {
      // --- triangular prism net (right triangular base) ---
      const x0 = 100, y0 = 60;
      const B = base * scale;
      const H = triHeight * scale;
      const L = prismLength * scale;

      const g = svg.append("g")
                   .attr("fill", "#e3f2fd")
                   .attr("stroke", "#1565c0")
                   .attr("stroke-width", 2);

      // two end triangles
      // left triangle
      g.append("polygon")
       .attr("points", `
         ${x0},${y0} 
         ${x0 + B},${y0} 
         ${x0},${y0 + H}
       `);
      // right triangle (translated by L)
      g.append("polygon")
       .attr("points", `
         ${x0 + L},${y0} 
         ${x0 + L + B},${y0} 
         ${x0 + L},${y0 + H}
       `);
      // lateral rectangle (base face)
      g.append("rect")
       .attr("x", x0)
       .attr("y", y0 + H + 20)
       .attr("width", L)
       .attr("height", B);

      // labels
      g.append("text")
       .attr("x", x0 + B/2).attr("y", y0 - 10)
       .attr("fill", "#1565c0").attr("text-anchor","middle").text("End faces");

      g.append("text")
       .attr("x", x0 + L/2).attr("y", y0 + H + 15)
       .attr("fill", "#1565c0").attr("text-anchor","middle").text("Lateral face");
    }
  }, [shape, length, width, height, base, triHeight, prismLength]);

  return (
    <div style={{ 
      border: "1px solid #ccc", 
      padding: 10, 
      borderRadius: 6, 
      marginBottom: 20 
    }}>
      <h4>Prism Explorer</h4>
      <p>Select your prism type and tweak its dimensions:</p>

      <div style={{ marginBottom: 12 }}>
        <label style={{ marginRight: 8 }}>
          Shape:
          <select
            value={shape}
            onChange={e => setShape(e.target.value)}
            style={{ marginLeft: 6 }}
          >
            <option value="rectangular">Rectangular Prism</option>
            <option value="triangular">Right Triangular Prism</option>
          </select>
        </label>

        {shape === "rectangular" ? (
          <>
            <label style={{ margin: "0 8px" }}>
              Length:
              <input
                type="number" step="0.1"
                value={length}
                onChange={e => setLength(parseFloat(e.target.value) || 0)}
                style={{ width: 60, marginLeft: 4 }}
              />
            </label>
            <label style={{ margin: "0 8px" }}>
              Width:
              <input
                type="number" step="0.1"
                value={width}
                onChange={e => setWidth(parseFloat(e.target.value) || 0)}
                style={{ width: 60, marginLeft: 4 }}
              />
            </label>
            <label style={{ margin: "0 8px" }}>
              Height:
              <input
                type="number" step="0.1"
                value={height}
                onChange={e => setHeight(parseFloat(e.target.value) || 0)}
                style={{ width: 60, marginLeft: 4 }}
              />
            </label>
          </>
        ) : (
          <>
            <label style={{ margin: "0 8px" }}>
              Base:
              <input
                type="number" step="0.1"
                value={base}
                onChange={e => setBase(parseFloat(e.target.value) || 0)}
                style={{ width: 60, marginLeft: 4 }}
              />
            </label>
            <label style={{ margin: "0 8px" }}>
              Height:
              <input
                type="number" step="0.1"
                value={triHeight}
                onChange={e => setTriHeight(parseFloat(e.target.value) || 0)}
                style={{ width: 60, marginLeft: 4 }}
              />
            </label>
            <label style={{ margin: "0 8px" }}>
              Length:
              <input
                type="number" step="0.1"
                value={prismLength}
                onChange={e => setPrismLength(parseFloat(e.target.value) || 0)}
                style={{ width: 60, marginLeft: 4 }}
              />
            </label>
          </>
        )}

        <label style={{ marginLeft: 8 }}>
          Unit:
          <select
            value={unit}
            onChange={e => setUnit(e.target.value)}
            style={{ marginLeft: 4 }}
          >
            <option>cm</option>
            <option>in</option>
            <option>m</option>
            <option>ft</option>
          </select>
        </label>
      </div>

      <svg
        ref={svgRef}
        width={svgW}
        height={svgH}
        style={{ background: "#fafafa", display: "block", margin: "0 auto 12px" }}
      />

      <p>
        <strong>Volume</strong> ={" "}
        {shape === "rectangular"
          ? "l × w × h"
          : "½ · base · height · length"}{" "}
        = {volume.toFixed(2)} {unit}³
      </p>
      <p>
        <strong>Surface Area</strong> ={" "}
        {shape === "rectangular"
          ? "2(lw + lh + wh)"
          : "2·(½·base·height) + (base + height + hyp)·length"}{" "}
        = {surfaceArea.toFixed(2)} {unit}²
      </p>

      <p style={{ fontSize: "0.9rem", color: "#555" }}>
        The little net above shows how each face would fold up into a prism.
      </p>
    </div>
  );
}
